import {
    createRouter,
    createWebHistory
} from "vue-router";
import {
    nextTick
} from 'vue'
import {
    store
} from "./Store"
import { createCanAccess } from '@/entitlements'
const canAccess = createCanAccess(store)
import { captureRef } from '@/affiliate'
import { isPrerender } from '@/utils/isPrerender'

import AmplifyEventBus from './plugins/aws-amplify-vue/events/AmplifyEventBus';
const FrontPage = () => import("./pages/FrontPage.vue");
const About = () => import('@/pages/About.vue')
const privacy = () => import('@/pages/Privacy.vue')
const TOS = () => import('@/pages/TOS.vue')
const Navbar = () => import("@/layout/Nav.vue");
const g25 = () => import('@/pages/g25.vue');
const fstats = () => import('@/pages/formalstats.vue');
const guides = () => import('@/pages/guides.vue');
const snpbrowser = () => import('@/pages/snpbrowser.vue');
const matches    = () => import('@/pages/matches.vue');
const traits     = () => import('@/pages/traits.vue');
const health = () => import('@/pages/health.vue');
const healthOAuthCallback = () => import('@/pages/healthOAuthCallback.vue');
const admix = () => import('@/pages/admixCalc.vue');
const discussions = () => import('@/pages/community.vue');
const affiliate = () => import('@/pages/affiliate.vue');
const partnerInvite = () => import('@/pages/PartnerInvite.vue');
const frame = () => import('./pages/iframe.vue');
const shares = () => import('./pages/shares.vue');
const sharefstats = () => import('./pages/sharefstats.vue');
const shareg25 = () => import('./pages/shareg25.vue');
const sharePca = () => import('./pages/sharePca.vue');
const shareAnalysis = () => import('./pages/shareAnalysis.vue');
const redeemShare = () => import('@/pages/RedeemShare.vue');
const labs = () => import('./pages/labs.vue');
const notfound = () => import('./pages/NotFound.vue');
const pca = () => import('./pages/pca.vue');
const previewSamplesModal = () => import('./pages/previewSamplesModal.vue');

const routes = [{
        path: "/",
        name: "landing",
        alias: ['/home', '/signedout', '/signin'],
        components: {
            default: FrontPage,
            header: Navbar
        },
        meta: {
            index: 0,
            title: 'Genoplot | Home',
            page: 1,
            deferNav: false
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: -1,
                transparent: true,
                component: 'home'
            }
        }
    },
    {
        // Vanity affiliate landing — genoplot.com/signup/influencerABC.
        //
        // Exists because an influencer can say a path out loud in a podcast or
        // a voiceover; nobody reads "?fpr=" aloud. Attribution itself is still
        // the vendor's job — this route only makes sure the vendor's script
        // gets the ref in the form it expects.
        //
        // Capture only — no hand-off to the tracking script, and deliberately
        // no full page reload.
        //
        // This used to window.location.replace() to /?fpr=<slug> so fpr.js
        // could read the param at init. Attribution now happens server-side:
        // the slug is persisted on the user record at account creation and a
        // DynamoDB stream registers the lead against FirstPromoter's track API.
        // That removed the reason for the reload, and two problems with it:
        //   - after a rename the slug is an alias, not the ref_id, so handing
        //     the raw value to fpr.js would have tracked against nothing
        //   - ad blockers commonly block firstpromoter.com, which silently lost
        //     the referral entirely
        //
        // Trade-off: fpr.js no longer sees these visits, so its CLICK stats
        // undercount. Attribution is unaffected. Restore by reinstating the
        // hard redirect if affiliates miss click-through numbers.
        path: "/signup/:ref",
        name: "affiliateSignup",
        components: {
            default: FrontPage,
            header: Navbar
        },
        meta: {
            index: 0,
            title: 'Genoplot | Home',
            page: 1,
            requiresAuth: false
        },
        beforeEnter: to => {
            captureRef(to.params.ref)
            // Land on home and pop the Sign Up modal — a referral visitor
            // should be invited to create an account, not just dropped on the
            // page. The slug is already in localStorage and is read again at
            // account creation (which may be days later), so attribution does
            // not depend on them signing up right now.
            //
            // Poll for Nav before emitting, mirroring the requiresAuth flow
            // below: on a cold load (a new visitor clicking the link) the
            // authState listener isn't mounted yet when this guard runs, so a
            // direct emit would be lost. Skip it if they're already signed in.
            nextTick(() => {
                let i = 0
                const id = setInterval(() => {
                    const ready = routerState.$tempstate && routerState.$tempstate.navBar
                    if (ready) {
                        const u = store.getters.get('user')
                        const signedIn = !!(u && u.username && u.username !== 'gusetUser')
                        if (!signedIn && routerState.$eventBus) {
                            routerState.$eventBus.emit('authState', 'signUp')
                        }
                    }
                    if (ready || i === 30) clearInterval(id)
                    i++
                }, 100)
            })
            return { path: '/' }
        }
    },
    {
        path: "/about",
        name: "about",
        components: {
            default: About,
            header: Navbar
        },
        meta: {
            index: 0,
            title: 'Genoplot | About',
            modala: 'aboutusmodal',
            params: {
                displayOnly: true
            },
            page: 1
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: 0,
                component: 'about'
            }
        }
    },
    {
        path: "/share/redeem/:code",
        name: "redeemShare",
        components: {
            default: redeemShare,
            header: Navbar
        },
        meta: {
            index: 0,
            title: 'Genoplot | Redeem Share',
            page: 2,
            requiresAuth: true,
            requiresMFA: false
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: 0
            }
        }
    },
    {
        path: "/labs",
        name: "labs",
        components: {
            default: labs,
            header: Navbar
        },
        meta: {
            index: 0,
            title: 'Genoplot | Labs',
            page: 2,
            requiresAuth: true,
            requiresMFA: false,
            save: true
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: 0,
                component: 'labs'
            }
        }
    },
    {
        path: "/calculators",
        name: "calculators",
        components: {
            default: admix,
            header: Navbar
        },
        meta: {
            index: 0,
            title: 'Genoplot | Calculators',
            page: 2,
            requiresAuth: true,
            save: true
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: 0,
                component: 'admix'
            }
        }
    },
    {
        path: "/admixpath",
        name: "admixpath",
        components: {
            default: admix,
            header: Navbar
        },
        meta: {
            index: 0,
            title: 'Genoplot | Calculators',
            page: 2,
            requiresAuth: true,
            save: true
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: 0,
                component: 'admix'
            }
        }
    },
    {
        path: "/admixold",
        name: "admixold",
        components: {
            default: admix,
            header: Navbar
        },
        meta: {
            index: 0,
            title: 'Genoplot | Calculators',
            page: 2,
            requiresAuth: true,
            save: true
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: 0,
                component: 'admix'
            }
        }
    },
    {
        path: "/admix",
        name: "admix",
        components: {
            default: admix,
            header: Navbar
        },
        meta: {
            index: 0,
            title: 'Genoplot | Admix',
            page: 2,
            requiresAuth: true,
            save: true
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: 0,
                component: 'admix'
            }
        }
    },
    {
        path: "/admixCalc",
        name: "admixCalc",
        components: {
            default: admix,
            header: Navbar
        },
        meta: {
            index: 0,
            title: 'Genoplot | Admix',
            page: 2,
            requiresAuth: true,
            save: true
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: 0,
                component: 'admix'
            }
        }
    },
    {
        path: "/admixplus",
        name: "AdmixPlus",
        components: {
            default: fstats,
            header: Navbar
        },
        meta: {
            index: 0,
            title: 'Genoplot | Admix Plus',
            page: 2,
            requiresAuth: true,
            rule: 'admixplus',
            save: true
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: 0,
                component: 'fstats'
            }
        }
    },
    {
        path: "/snpbrowser/:kitId?",
        name: "SnpBrowser",
        components: {
            default: snpbrowser,
            header: Navbar
        },
        meta: {
            index: 0,
            title: 'Genoplot | SNP Browser',
            page: 2,
            requiresAuth: true,
            rule: 'snpbrowser',
            save: true
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: 0,
                component: 'snpbrowser'
            }
        }
    },
    {
        path: "/matches/:kitId?",
        name: "Matches",
        components: {
            default: matches,
            header: Navbar
        },
        meta: {
            index: 0,
            title: 'Genoplot | Ancestry Matches',
            page: 2,
            requiresAuth: true,
            rule: 'snpbrowser',
            save: true
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: 0,
                component: 'matches'
            }
        }
    },
    {
        path: "/traits/:kitId?",
        name: "Traits",
        components: {
            default: traits,
            header: Navbar
        },
        meta: {
            index: 0,
            title: 'Genoplot | Trait Variants',
            page: 2,
            requiresAuth: true,
            rule: 'snpbrowser',
            save: true
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: 0,
                component: 'traits'
            }
        }
    },
  {
        path: "/health",
        name: "health",
        components: {
            default: health,
            header: Navbar
        },
        meta: {
            // `rule: 'health'` removed — the Health Dashboard is now
            // available to everyone (signed in). `requiresAuth: true`
            // still gates unsigned visitors through the sign-in flow.
            title: 'Genoplot | Health',
            page: 2,
            requiresAuth: true,
            save: true
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: 0,
                component: 'health'
            }
        }
    },
    {
        path: "/health/oauth/:provider/callback",
        name: "HealthOAuthCallback",
        components: {
            default: healthOAuthCallback,
            header: Navbar
        },
        meta: {
            index: 0,
            title: 'Genoplot | Connecting…',
            page: 2,
            requiresAuth: true,
            save: false
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: 0,
                component: 'health'
            }
        }
    },
    {
        path: "/shared/fstats",
        name: "shareFstats",
        components: {
            default: sharefstats,
            header: Navbar
        },
        meta: {
            index: 0,
            title: 'Genoplot | Shared Results',
            page: 2,
            requiresAuth: false,
            save: false
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: 0,
                component: 'shares'
            }
        }
    },
    {
        path: "/shared/g25",
        name: "shareG25",
        components: {
            default: shareg25,
            header: Navbar
        },
        meta: {
            index: 0,
            title: 'Genoplot | Shared G25 Results',
            page: 2,
            requiresAuth: false,
            save: false
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: 0,
                component: 'shares'
            }
        }
    },
    {
        path: "/shared/admix",
        name: "shareAdmix",
        components: {
            default: shareg25,
            header: Navbar
        },
        meta: {
            index: 0,
            title: 'Genoplot | Shared Admix Results',
            page: 2,
            requiresAuth: false,
            save: false
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: 0,
                component: 'shares'
            }
        }
    },
    {
        path: "/shared/pca",
        name: "sharePca",
        components: {
            default: sharePca,
            header: Navbar
        },
        meta: {
            index: 0,
            title: 'Genoplot | Shared PCA',
            page: 2,
            requiresAuth: false,
            save: false
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: 0,
                component: 'shares'
            }
        }
    },
    {
        path: "/shared/analysis",
        name: "shareAnalysis",
        components: {
            default: shareAnalysis,
            header: Navbar
        },
        meta: {
            index: 0,
            title: 'Genoplot | Shared Analysis',
            page: 2,
            requiresAuth: false,
            save: false
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: 0,
                component: 'shares'
            }
        }
    },
    {
        path: "/shares/:pathMatch(.*)*",
        name: "shares",
        components: {
            default: shares,
            header: Navbar
        },
        meta: {
            index: 0,
            title: 'Genoplot | Shares',
            page: 2,
            requiresAuth: false,
            save: true
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: 20,
                component: 'shares'
            }
        }
    },
    {
        path: "/faq",
        name: "faq",
        components: {
            default: frame,
            header: Navbar
        },
        meta: {
            index: 0,
            title: 'Genoplot | FAQ',
            page: 2,
            requiresAuth: false,
            save: true,
            qs: [
                ['customname', 'FAQ']
            ],
            frameroute: 'category/2/help'
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: 0,
            }
        }
    },
    {
        path: "/news",
        name: "news",
        components: {
            default: frame,
            header: Navbar
        },
        meta: {
            index: 0,
            title: 'Genoplot | News',
            page: 2,
            requiresAuth: false,
            save: true,
            qs: [
                ['customname', 'NEWS']
            ],
            frameroute: 'category/1/news'
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: 0,
            }
        }
    },
    {
        path: "/version",
        name: "version",
        components: {
            default: frame,
            header: Navbar
        },
        meta: {
            index: 0,
            title: 'Genoplot | Version History',
            page: 2,
            requiresAuth: false,
            save: true,
            qs: [
                ['customname', 'VERSION']
            ],
            frameroute: 'topic/411/genoplot-version-history'
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: 0,
            }
        }
    },
    {
        path: "/discussions/:pathMatch(.*)*",
        name: "discussions",
        components: {
            default: discussions,
            header: Navbar
        },
        meta: {
            index: 0,
            title: 'Genoplot | Discussions',
            page: 2,
            requiresAuth: false,
            save: true,
            qs: [],
            frameroute: ''
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: 0,
                component: 'discussions'
            }
        }
    },
    {
        // Invite-only partner onboarding — genoplot.com/partner/invite?code=<code>.
        //
        // Deliberately never linked anywhere in the app: it's reached only via
        // a single-use invite sent out-of-band. requiresAuth so a new recipient
        // signs up first and the invite binds to their verified account; the
        // guard stores the full path (query included) and returns them here
        // afterwards, so the same link works for existing and brand-new users.
        //
        // The code is a QUERY param, not a path param, on purpose: the guard's
        // whitelist keeps the raw ":param" syntax in its entries, so a path
        // like /partner/invite/:code never substring-matches a real URL and the
        // requiresAuth block is skipped entirely (see the standing whitelist
        // bug). A static path avoids that and reliably enforces auth — which
        // here is the difference between a new user getting the sign-up flow
        // and hitting a dead page.
        //
        // No meta.rule/meta.group: redemption is what GRANTS affiliate access,
        // so gating the page on already having it would be circular.
        path: "/partner/invite",
        name: "partnerInvite",
        components: {
            default: partnerInvite,
            header: Navbar
        },
        meta: {
            index: 0,
            title: 'Genoplot | Partner Invite',
            page: 2,
            requiresAuth: true,
            requiresMFA: false
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: 0
            }
        }
    },
    {
        // Embedded FirstPromoter affiliate dashboard.
        //
        // Deliberately a plain path, NOT `/affiliate/:id`: FirstPromoter's docs
        // require the token-bearing iframe to sit behind auth and out of reach
        // of crawlers, and param routes currently skip the `requiresAuth` check
        // entirely (the guard's whitelist keeps the raw `:param` syntax in its
        // entries, so the substring test never matches). Nothing here needs a
        // param anyway — the promoter is resolved server-side from the JWT.
        path: "/affiliate",
        name: "affiliate",
        components: {
            default: affiliate,
            header: Navbar
        },
        meta: {
            index: 0,
            title: 'Genoplot | Affiliate Dashboard',
            page: 2,
            requiresAuth: true,
            requiresMFA: false,
            // Entitlement rule reading `subscription.affiliate` off the user
            // record — NOT a Cognito group. Group membership is an ID-token
            // claim, so it only lands when the token refreshes (an hour by
            // default); the user record is pushed to the client on every
            // change, so flipping the flag takes effect immediately.
            // Enforced again in the promoterSession/enrollPromoter lambdas,
            // which read the same field server-side.
            rule: 'affiliate'
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: 0,
                component: 'affiliate'
            }
        }
    },
    {
        path: "/preview-samples-modal",
        name: "previewSamplesModal",
        components: {
            default: previewSamplesModal
        },
        meta: {
            title: 'Genoplot | Preview SamplesModal',
            requiresAuth: false,
            page: 1
        }
    },
    {
        path: "/login",
        name: "login",
        components: {
            default: FrontPage,
            header: Navbar
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: 950,
                component: 'home'
            }
        },
        meta: {
            modal: 'login-modal',
            title: 'Genoplot | Login',
            page: 1
        }
    },
    {
        path: "/demo",
        name: "demo",
        components: {
            default: FrontPage,
            header: Navbar
        },
        meta: {
            modal: 'demo-modal',
            title: 'Genoplot | Demo',
            page: 1
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: 950,
                component: 'home'
            }
        }
    },
    {
        path: "/subscribe",
        name: "subscribe",
        components: {
            default: FrontPage,
            header: Navbar
        },
        meta: {
            modal: 'subscribe',
            requiresAuth: true,
            title: 'Genoplot | Subscriptions',
            params: {
                displayOnly: true
            }
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: 950,
                component: 'home'
            }
        }
    },
    {
        path: "/privacy",
        name: "privacy",
        components: {
            default: privacy,
            header: Navbar
        },
        meta: {
            index: 0,
            title: 'Genoplot | Privacy',
            modala: 'tosmodal',
            params: {
                displayOnly: true
            },
            page: 8
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: 0,
                component: 'TOS'
            }
        }
    },
    {
        path: "/tos",
        name: "tos",
        components: {
            default: TOS,
            header: Navbar
        },
        meta: {
            index: 0,
            title: 'Genoplot | TOS',
            modala: 'tosmodal',
            params: {
                displayOnly: true
            },
            page: 8
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: 0,
                component: 'TOS'
            }
        }
    },
    {
        path: "/g25",
        name: "g25",
        components: {
            default: g25,
            header: Navbar,
        },
        meta: {
            page: 2,
            title: 'Genoplot | nMonte Runner',
            requiresAuth: true,
            save: true,
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: 0,
                component: 'g25'
            }
        }
    },
    {
        path: "/pca",
        name: "pca",
        components: {
            default: pca,
            header: Navbar
        },
        meta: {
            page: 2,
            title: 'Genoplot | PCA',
            requiresAuth: true,
            save: true,
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: 0,
                component: 'pca'
            },
        }
    },
    {
        path: "/contactus",
        name: "contact",
        components: {
            default: notfound,
            header: Navbar
        },
        beforeEnter: (to, from) => {
            if (!from.name) {
                return '/'
            } else {
                store.commit('change', {
                    contacted: true
                })
                return false
            }
        }
    },
    //- /help used to be a side-effect route that opened the Zendesk widget and
    //- cancelled navigation; it never rendered anything. Guides replaces it.
    //- Support is unaffected — the Intercom launcher is on every page — and the
    //- path is kept as a redirect so existing links still land somewhere useful.
    {
        path: "/help",
        redirect: { name: "guides" }
    },
    {
        path: "/guides/:slug?",
        name: "guides",
        components: {
            default: guides,
            header: Navbar
        },
        meta: {
            index: 0,
            title: 'Genoplot | Guides',
            page: 2,
            requiresAuth: false,
            save: true
        },
        props: {
            header: {
                position: 'fixed',
                colorOnScroll: 0,
            }
        }
    },
    {
        path: "/:pathMatch(.*)*",
        name: "notfound",
        components: {
            default: notfound,
            header: Navbar
        }
    }
]
const whitelist = routes.map(x => x.path.replace('/', '')).filter(x => x && !x.includes('*'))

// Shared state for router globals (replaces Vue.prototype usage)
const routerState = {
    routed: {},
    $tempstate: null,
    $eventBus: null,
    $auth: null,
}
// Allow main.js to inject these after app creation
export function setRouterGlobals(globals) {
    Object.assign(routerState, globals)
}

const router = createRouter({
    history: createWebHistory('/'),
    routes,
    scrollBehavior(to, from, savedPosition) {
        const scrollContainer = document.getElementById('app')
        return new Promise((resolve) => {
            if (!savedPosition && !to.hash && scrollContainer && scrollContainer.xyz) {
                scrollContainer.scrollTo(0, 0);
            } else {
                nextTick(() => {
                    if (scrollContainer) {
                        if (savedPosition) {
                            scrollContainer.scrollTo(savedPosition.left, savedPosition.top);
                        } else if (to.hash) {
                            const element = document.querySelector(to.hash);
                            if (element) {
                                const elementPosition = element.offsetTop;
                                scrollContainer.scrollTo({
                                    top: elementPosition,
                                    behavior: 'smooth'
                                });
                            } else {
                                scrollContainer.scrollTo({
                                    top: 0,
                                    behavior: 'smooth'
                                });
                            }
                        } else {
                            scrollContainer.scrollTo({
                                top: 0,
                                behavior: 'smooth'
                            });
                        }
                    }
                    resolve({
                        left: 0,
                        top: 0
                    });
                });
            }
        });
    }
})

const waitForStorageToBeReady = async (to, from) => {
    window.routeStart = Date.now()
    if (to?.path === from?.path) return;
    console.log('started change')
    let started = Date.now()
    await store.restored
}

router.afterEach((to, from) => {
    if (to?.path === from?.path) return;
    if (store.getters.get('user') && to.name && to.path !== '/') {
        localStorage.setItem(store.getters.get('user').username + ':defaultRoute', to.name)
    }
    if (from.meta.showmfa && from.meta.modal) {
        nextTick(() => {
            AmplifyEventBus.emit('appEvents', from.meta.modal)
            return;
        })
    }
    if (to.meta.modal && !from.name) {
        let params = to.meta.params || {};
        if (to.name == 'login') {
            setTimeout(() => {
                AmplifyEventBus.emit('authState', 'signIn')
            }, 200);
        } else if (to.name == 'subscribe') {
            setTimeout(() => {
                AmplifyEventBus.emit('appEvents', {
                    exec: 'checkSubStatus',
                    component: 'nav',
                    params: {
                        ...{
                            locked: true,
                            modal: to.meta.modal
                        },
                        ...params
                    }
                })
            }, 0);
        } else {
            return false
            setTimeout(() => {
                AmplifyEventBus.emit('appEvents', {
                    exec: 'showModal',
                    component: 'nav',
                    params: {
                        ...{
                            locked: true,
                            modal: to.meta.modal
                        },
                        ...params
                    }
                })
            }, 0);
        }
    }
    if (to.hash) {
        nextTick(() => {
            let timeout = 0
            setTimeout(() => {
                document.getElementById(to.hash.replace(/#/, '')).scrollIntoView({
                    alignToTop: false,
                    behavior: 'smooth'
                })
            }, timeout)
        })
    } else {
        if (document.getElementById('appaa')) {
            nextTick(() => {
                setTimeout(() => {
                    document.getElementById('app').scrollTo({
                        top: 0,
                        left: 0,
                        behavior: 'smooth'
                    })
                }, 0)
            })
        }
    }
    AmplifyEventBus.emit('appEvents', {
        exec: 'routeChange',
        component: 'nav',
        params: {
            to: to,
            from: from
        }
    })
});
// Emit a beforeRouteChange event on actual path changes so
// Nav-level UI that should collapse BEFORE the new page mounts
// (the shareNavDrawer, mainly) has a chance to react without
// flashing stale content over the outgoing view. Same
// {to, from} shape as the afterEach routeChange emit so
// downstream handlers can share the guard logic.
router.beforeEach((to, from) => {
    if (to?.path && from?.path && to.path !== from.path) {
        AmplifyEventBus.emit('appEvents', {
            exec: 'beforeRouteChange',
            component: 'nav',
            params: { to, from }
        })
    }
})
router.beforeEach(waitForStorageToBeReady)
router.beforeEach(async (to, from) => {
    if (!to?.path || to?.path === from?.path || !whitelist.some(x => to.path.includes(x))) return;
    const groups = store.getters.get('usergroups') || []
    // Entitlement (`to.meta.rule`) check used to live here, BEFORE
    // the `requiresAuth` block below. That ordering was wrong:
    // `ENTITLEMENTS` is only populated by Nav.vue.js AFTER sign-in,
    // so unsigned visitors to a rule-gated path (e.g. `/admixplus`)
    // would fail `canAccess` on a null ENTITLEMENTS map, get
    // silently redirected to `/`, and never see the sign-in prompt
    // the requiresAuth flow would have raised. Moved into
    // `postAuth` so the rule only evaluates once auth is confirmed
    // AND the post-sign-in hydration has had a chance to populate
    // ENTITLEMENTS.
    console.log(`Route: ${Date.now() - routeStart} ms`)
    store.commit('change', {
        target: false
    });
    const nearestWithTitle = to.matched.slice().reverse().find(r => r.meta && r.meta.title)
    let env = window.location.hostname.split('.')[0] === "staging" ? 'staging' : window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname === '0.0.0.0' || window.location.hostname == 'dev.genoplot.com' || window.location.hostname == '192.168.86.39' || window.location.hostname.substr(0, 3) == "10." ? 'development' : 'production'
    store.commit('change', {
        env: env
    })
    env = store.getters.get('env') === 'development' ? 'DEV ' : store.getters.get('env') === 'staging' ? 'Staging ' : ''
    if (to.name == 'insights' && env != 'DEV ') {
        console.log('Returning home', env);
        return {
            path: '/'
        }
    }
    if (nearestWithTitle) document.title = env + nearestWithTitle.meta.title
    if (to.matched.some(record => record.meta.requiresAuth || record.meta.requiresMFA)) {
        let firstLoginTarget = localStorage.getItem('firstLoginTarget')
        if (firstLoginTarget) localStorage.removeItem('firstLoginTarget')
        store.commit('change', {
            target: to.name == 'discussions' ? to.fullPath.replace(/^\//, '') : firstLoginTarget ? firstLoginTarget.replace(/^\//, '') : to.fullPath.replace(/^\//, '')
        });
        let userAgent = isPrerender()
        if (userAgent && !store.getters.get('user')) {
            store.commit('change', {
                user: {
                    username: 'gusetUser'
                }
            });
            return postAuth(to, from)
        } else if (!window.sessionStorage.getItem('user')) {
            let signedin = window.localStorage.getItem('CognitoIdentityServiceProvider.7k97gav5nh6fi0ql94lm0io4aj.LastAuthUser') == null ? false : true
            if (signedin) {
                routerState.$eventBus && routerState.$eventBus.emit('authState', 'signedIn')
                console.log('router signedIn');
                return postAuth(to, from)
            } else {
                let user
                try {
                    user = routerState.$auth ? await routerState.$auth.currentAuthenticatedUser() : false
                    if (user) {
                        store.commit('change', {
                            user: {
                                username: user.username,
                                sub: user.attributes.sub,
                                email: user.attributes.email
                            }
                        });
                        sessionStorage.setItem('user', JSON.stringify(store.getters.get('user')))
                    }
                } catch (err) {
                    console.log('user route', err)
                    user = false;
                }
                if (!(user && user.username)) {
                    console.log('no user route')
                    store.commit('change', {
                        target: to.fullPath.replace(/^\//, '')
                    });
                    try {
                        var init = JSON.parse(store.getters.get('init'))
                    } catch (err) {
                        console.log(err)
                        var init = false;
                    }
                    if (init) {
                        init.target = to.fullPath.replace(/^\//, '')
                        store.commit('change', {
                            init: JSON.stringify(init)
                        });
                    }
                    nextTick(() => {
                        console.log('signin');
                        let mounted = false
                        sessionStorage.setItem('firstLoginTarget', to.fullPath)
                        let i = 0;
                        let intervalId = setInterval(function() {
                            if (routerState.$tempstate && routerState.$tempstate.navBar) {
                                mounted = true
                                routerState.$eventBus && routerState.$eventBus.emit('authState', 'signIn')
                            }
                            if (i === 30 || mounted) {
                                clearInterval(intervalId);
                            }
                            i++;
                        }, 100);
                        store.commit('change', {
                            spinner: false
                        })
                    })
                    return {
                        path: '/'
                    }
                } else {
                    return postAuth(to, from)
                }
            }
        } else {
            if (to.meta.requiresMFA) {
                if (!from || !from.name) {
                    from.meta.toname = to.name
                    from.meta.showmfa = true
                    from.meta.modal = {
                        exec: 'showModal',
                        component: 'nav',
                        params: {
                            pred: 'verifyToken',
                            token: `token-${to.name}`,
                            modal: 'VerifyMfaModal',
                            action: {
                                route: to.name
                            }
                        }
                    }
                    store.commit('change', {
                        target: to.fullPath.replace(/^\//, '')
                    });
                    return {
                        path: '/'
                    }
                } else {
                    AmplifyEventBus.emit('appEvents', {
                        exec: 'showModal',
                        component: 'nav',
                        params: {
                            pred: 'verifyToken',
                            token: `token-${to.name}`,
                            modal: 'VerifyMfaModal',
                        }
                    })
                    return false;
                }
            } else {
                return postAuth(to, from)
            }
        }
    } else {
        store.commit('change', {
            target: to.fullPath.replace(/^\//, '')
        });
        return postAuth(to, from)
    }
    console.log(`Route finished ${Date.now() - routeStart} ms`)
})

function postAuth(to, from) {
    // Entitlement check — this is the correct location: postAuth
    // is the sync point that ALL auth-confirmed branches in
    // beforeEach funnel through. By the time we're here, the
    // user is authenticated (or believed to be via Cognito cache).
    // Unauthenticated users NEVER reach this function — their
    // path is the `return { path: '/' }` + signIn emit inside the
    // `requiresAuth` block.
    //
    // Gate: only evaluate the rule when ENTITLEMENTS have actually
    // been hydrated by Nav.vue.js (the `SET_ENTITLEMENTS_READY`
    // flag). The Cognito-cached-signin branch calls postAuth
    // before Nav has had a chance to fetch them — skipping the
    // check in that narrow window lets the route load, Nav
    // hydrates, and any subsequent navigation evaluates the rule
    // against a populated map. Without the gate, a missing
    // `ENTITLEMENTS[rule]` lookup returns false, bouncing a
    // legitimately-entitled user to `/`.
    const entitlementsReady = !!store.getters.get('SET_ENTITLEMENTS_READY')
    if (entitlementsReady && to.meta && to.meta.rule && !canAccess(to.meta.rule)) {
        console.log('[router] entitlement check failed for', to.name, '— redirecting to /')
        return { path: '/' }
    }

    if (from.meta.modal) {
        let params = to.params || {};
        AmplifyEventBus.emit('appEvents', {
            exec: 'closeModal',
            component: 'nav',
            params: {
                modal: to.meta.modal
            }
        })
    }
    if (to.meta.modal && from.name) {
        let params = to.params || {};
        if (to.name == 'subscribe') {
            setTimeout(() => {
                AmplifyEventBus.emit('appEvents', {
                    exec: 'checkSubStatus',
                    component: 'nav',
                    params: {
                        ...{
                            locked: false,
                            modal: to.meta.modal
                        },
                        ...params
                    }
                })
            }, 0);
        } else {
            AmplifyEventBus.emit('appEvents', {
                exec: 'showModal',
                component: 'nav',
                params: {
                    ...{
                        modal: to.meta.modal,
                        locked: from.path === '/' ? false : false
                    },
                    ...params
                }
            })
            return false;
        }
        if (from.path !== '/' && to.name !== from.name) {
            return false;
        }
    } else {
        if (to.meta.index !== undefined) {
            if (to.meta.index !== store.getters.get('currentIndex')) {
                AmplifyEventBus.emit('appEvents', {
                    exec: 'goToSlide',
                    component: 'frontPage',
                    params: {
                        index: to.meta.index,
                        speed: to.meta.speed ? to.meta.speed : 0
                    }
                })
                store.commit('change', {
                    currentIndex: to.meta.index
                })
            }
        }
    }
}

export default router
